/**
 * 配信先タブ：検索・リスト・削除。
 * 仕様書: docs/01_spec/spec_mail_system.md B-10.3
 */
import { useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Download, MailWarning, RefreshCw, Search, Tag, Upload, UserPlus, Users } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { BulkTagDialog } from './BulkTagDialog';
import { ContactCreateDialog } from './ContactCreateDialog';
import { ContactEditDialog } from './ContactEditDialog';
import { ContactImportDialog } from './ContactImportDialog';
import { ContactRow } from './ContactRow';
import { exportContactsCsv, exportContactsXlsx } from './contactsExport';
import { InvalidContactsDialog } from './InvalidContactsDialog';

import { useAuth } from '@/hooks/useAuth';

import { EmptyState } from '@/components/common/EmptyState';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import {
  invalidateMailContactsCache,
  useMailContacts,
  type MailContactRecord,
} from '@/hooks/useMailContacts';
import { useMailGroups, type MailGroupRecord } from '@/hooks/useMailGroups';
import { getCollectionCacheInfo } from '@/lib/collectionCache';


function formatSyncRelative(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'たった今';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}分前`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}時間前`;
  return `${Math.floor(diff / 86400_000)}日前`;
}

export function ContactsTab() {
  const qc = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [invalidOpen, setInvalidOpen] = useState(false);
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [editTarget, setEditTarget] = useState<MailContactRecord | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // 最終同期時刻表示を 30秒ごとに更新
  const [, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      invalidateMailContactsCache();
      await qc.invalidateQueries({ queryKey: ['mail_contacts'] });
    } finally {
      setRefreshing(false);
    }
  };
  const { data, isLoading } = useMailContacts({ limit: 5000 });
  const { data: groups } = useMailGroups();

  const groupMap = useMemo(() => {
    const map = new Map<string, MailGroupRecord>();
    for (const g of groups ?? []) map.set(g.id, g);
    return map;
  }, [groups]);

  const filtered = useMemo(() => {
    const list = data ?? [];
    const k = keyword.trim().toLowerCase();
    if (!k) return list;
    return list.filter((c) => {
      if (c.name?.toLowerCase().includes(k)) return true;
      if (c.emails?.some((e) => e.address.toLowerCase().includes(k))) return true;
      if (c.tags?.some((t) => t.toLowerCase().includes(k))) return true;
      for (const v of Object.values(c.customFields ?? {})) {
        if (typeof v === 'string' && v.toLowerCase().includes(k)) return true;
      }
      // 所属グループ名でも検索ヒット
      for (const gid of c.groupIds ?? []) {
        const g = groupMap.get(gid);
        if (g?.name.toLowerCase().includes(k)) return true;
      }
      return false;
    });
  }, [data, keyword, groupMap]);

  // 仮想スクロール: スクロール対象は祖先の .screen-body（overflow-y:auto）。
  // 自前で scroll element を作らず既存レイアウトを維持するため、
  // sentinel から scrollable な祖先を探し、scrollMargin で位置補正する。
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [scrollParent, setScrollParent] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    // sentinel は isLoading 後に出現するため、scrollParent 未設定時のみ探索する
    if (scrollParent || !sentinelRef.current) return;
    let el: HTMLElement | null = sentinelRef.current.parentElement;
    while (el) {
      const style = getComputedStyle(el);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        setScrollParent(el);
        return;
      }
      el = el.parentElement;
    }
  }, [isLoading, scrollParent]);

  useEffect(() => {
    if (!sentinelRef.current || !scrollParent) return;
    const update = () => {
      if (!sentinelRef.current || !scrollParent) return;
      const sRect = sentinelRef.current.getBoundingClientRect();
      const pRect = scrollParent.getBoundingClientRect();
      // sentinel の "スクロール内容上端からの距離" を算出
      setScrollMargin(sRect.top - pRect.top + scrollParent.scrollTop);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(scrollParent);
    if (sentinelRef.current.parentElement) ro.observe(sentinelRef.current.parentElement);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [scrollParent, isLoading]);

  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollParent,
    estimateSize: () => 72,
    overscan: 8,
    scrollMargin,
    getItemKey: (index) => filtered[index]?.id ?? index,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  // 書き出し: 検索フィルター適用中は表示中のリスト (filtered) をそのまま対象にする
  const handleExport = (format: 'csv' | 'xlsx') => {
    if (filtered.length === 0) {
      toast.error('書き出し対象がありません');
      return;
    }
    try {
      if (format === 'csv') exportContactsCsv(filtered, groupMap);
      else exportContactsXlsx(filtered, groupMap);
      toast.success(`${filtered.length} 件を書き出しました`);
    } catch (err) {
      toast.error(
        `書き出し失敗: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[var(--text-muted)] pointer-events-none" />
          <Input
            type="search"
            placeholder="メアド / 名前 / タグ / グループ / カスタムフィールド値で検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={handleRefresh}
          disabled={refreshing}
          title="サーバーから再取得 (キャッシュをクリア)"
        >
          <RefreshCw className={`size-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
        <Button type="button" variant="secondary" onClick={() => setCreateOpen(true)}>
          <UserPlus className="size-4 mr-1" />
          新規配信先
        </Button>
        <Button type="button" variant="secondary" onClick={() => setBulkTagOpen(true)}>
          <Tag className="size-4 mr-1" />
          一括タグ付与
        </Button>
        {isOwner && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => setInvalidOpen(true)}
            title="送信失敗（501等）の無効アドレスを配信先から整理"
            className="text-[var(--accent-amber)]"
          >
            <MailWarning className="size-4 mr-1" />
            無効アドレス整理
          </Button>
        )}
        <Button type="button" variant="primary" onClick={() => setImportOpen(true)}>
          <Upload className="size-4 mr-1" />
          CSV 取り込み
        </Button>
      </div>

      <div className="text-xs text-[var(--text-muted)] flex items-center gap-2">
        <span>
          {isLoading ? '読み込み中…' : `${filtered.length} 件 / 全 ${data?.length ?? 0} 件`}
        </span>
        {(() => {
          const info = getCollectionCacheInfo('mail_contacts');
          if (!info) return null;
          return (
            <span className="text-[10px] text-[var(--text-muted)]">
              · ローカルキャッシュ: {formatSyncRelative(info.fetchedAtMs)}に同期
            </span>
          );
        })()}
        <div className="ml-auto flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleExport('csv')}
            disabled={isLoading || filtered.length === 0}
            title={keyword ? '検索で絞り込まれた表示中のリストを CSV 書き出し' : '表示中のリストを CSV 書き出し'}
          >
            <Download className="size-3.5 mr-1" />
            CSV 書き出し
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => handleExport('xlsx')}
            disabled={isLoading || filtered.length === 0}
            title={keyword ? '検索で絞り込まれた表示中のリストを Excel 書き出し' : '表示中のリストを Excel 書き出し'}
          >
            <Download className="size-3.5 mr-1" />
            Excel 書き出し
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={keyword ? '一致する配信先がありません' : '配信先がまだ登録されていません'}
          description={
            keyword
              ? 'キーワードを変更するか CSV から取り込んでください'
              : '右上の「CSV 取り込み」から顧客リストを登録できます'
          }
        />
      ) : (
        <div
          ref={sentinelRef}
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: 'relative',
            width: '100%',
          }}
        >
          {virtualItems.map((vi) => {
            const c = filtered[vi.index];
            if (!c) return null;
            return (
              <div
                key={vi.key}
                data-index={vi.index}
                ref={rowVirtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vi.start - rowVirtualizer.options.scrollMargin}px)`,
                  paddingBottom: 8,
                }}
              >
                <ContactRow
                  contact={c}
                  groupMap={groupMap}
                  onEdit={setEditTarget}
                />
              </div>
            );
          })}
        </div>
      )}

      <ContactImportDialog open={importOpen} onOpenChange={setImportOpen} />
      <ContactCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <BulkTagDialog open={bulkTagOpen} onOpenChange={setBulkTagOpen} />
      <InvalidContactsDialog open={invalidOpen} onOpenChange={setInvalidOpen} />
      <ContactEditDialog
        open={!!editTarget}
        onOpenChange={(o) => {
          if (!o) setEditTarget(null);
        }}
        contact={editTarget}
      />
    </div>
  );
}
